import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef} from '@angular/material/dialog';

import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {OpportunityStatuses} from '../../interfaces/opportunity-statuses.enum';
import {OpportunityStages} from '../../interfaces/opportunity-stages.enum';
import {OpportunitySectors} from '../../interfaces/opportunity-sectors.enum';
import {OpportunityCustomerTypes} from '../../interfaces/opportunity-customer-types.enum';
import {DataService} from '../../../services/data.service';
import {filter, map, take, takeUntil, tap} from 'rxjs/operators';
import {Observable, Subject} from 'rxjs';
import {Opportunity} from '../../interfaces/opportunity.interface';
import {AuthService} from '../../../services/auth.service';
import {SearchModes} from '../../../interfaces';
import {OpportunityService} from '../../services/opportunity.service';
import {Company} from '../../interfaces/company.interface';
import {Moment} from 'moment-timezone';
import {UserPickerComponent} from '../../../shared/components/user-picker.component';
import {MatOption} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';
import {MatFormFieldModule} from '@angular/material/form-field';
import {CompanySearchComponent} from '../../../shared/company-search/company-search.component';
import {CountrySelectorComponent} from '../../../shared/country-selector/country-selector.component';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatDivider} from '@angular/material/list';
import {MatProgressBar} from '@angular/material/progress-bar';
import {AsyncPipe, KeyValuePipe} from '@angular/common';
import {MatInput} from '@angular/material/input';
import {MatButton} from '@angular/material/button';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';



@Component({
    selector: 'opportunity-form',
    templateUrl: 'opportunity-form.component.html',
    imports: [
        UserPickerComponent,
        MatSelect,
        MatFormFieldModule,
        MatOption,
        CompanySearchComponent,
        CountrySelectorComponent,
        MatDatepickerModule,
        MatCheckbox,
        MatDivider,
        MatProgressBar,
        KeyValuePipe,
        AsyncPipe,
        ReactiveFormsModule,
        MatInput,
        MatDialogActions,
        MatDialogContent,
        MatButton,
        MatDialogClose,
        CdkTextareaAutosize
    ],
    styleUrls: [
        'opportunity-form.component.scss'
    ]
})
export class OpportunityFormComponent implements OnInit, OnDestroy {

    private unsubscribe$: Subject<void> = new Subject<void>();

    private today = new Date();
    crateLinkedBudget = new UntypedFormControl(true);

    readonly OpportunitySectors = OpportunitySectors;
    readonly OpportunityCustomerTypes = OpportunityCustomerTypes;
    readonly OpportunityStages = [
        OpportunityStages.PRIMO_CONTATTO,
        OpportunityStages.RICHIESTA_DI_OFFERTA,
        OpportunityStages.OFFERTA_INVIATA
    ];

    private selectedCompany: any;

    formGroup: UntypedFormGroup = new UntypedFormGroup({
        id: new UntypedFormControl(null),
        budgetNr: new UntypedFormControl(null),
        budgetId: new UntypedFormControl(null),
        companyName: new UntypedFormControl(null, [Validators.required]),
        contactId: new UntypedFormControl(null),
        companyId: new UntypedFormControl(null),
        country: new UntypedFormControl(null, [Validators.required]),
        opportunityName: new UntypedFormControl(null, [Validators.required]),
        status: new UntypedFormControl(OpportunityStatuses.IN_PROGRESS),
        opportunityStage: new UntypedFormControl(OpportunityStages.PRIMO_CONTATTO),
        proposalSentOn: new UntypedFormControl(null),
        proposalRequested: new UntypedFormControl(null),
        nextFollowUpDate: new UntypedFormControl(new Date(new Date(this.today).setDate(this.today.getDate() + 14)), [Validators.required]),
        followUpDate: new UntypedFormControl(new Date()),
        nextSteps: new UntypedFormControl(null),
        salesManager: new UntypedFormControl(null),
        sector: new UntypedFormControl(null, [Validators.required]),
        customerType: new UntypedFormControl(null, [Validators.required]),
        potentialClosureQuarter: new UntypedFormControl(null, [Validators.required]),
        potentialClosureYear: new UntypedFormControl(null, [Validators.required]),
        revisionNumber: new UntypedFormControl(0, [Validators.required]),
        lostBecause: new UntypedFormControl(null),

        currency: new UntypedFormControl('EUR'),
        exchangeRate: new UntypedFormControl(null),
        forecastAmountITA: new UntypedFormControl(null),
        forecastAmountUSA: new UntypedFormControl(null),

        createdBy: new UntypedFormControl(this.authService.currentUser.email),
        createdAt: new UntypedFormControl(this.today),
        latestFollowUp: new UntypedFormControl(null),
        searchMode: new UntypedFormControl(SearchModes.QUICK),
        isChangeOrder: new UntypedFormControl(false)
    });

    salesManagerControl = new UntypedFormControl();
    companySearchControl = new UntypedFormControl(null);

    exchangeRate$: Observable<any>;

    constructor(
        private dialogRef: MatDialogRef<OpportunityFormComponent>,
        private dataService: DataService,
        private authService: AuthService,
        private opportunityService: OpportunityService
    ) {
    }

    ngOnInit() {
        this.exchangeRate$ = this.dataService.getExchangeRateInternal('USD').pipe(
            take(1),
            map(r => r?.rates ? r.rates['USD'] : 1.14),
            tap(r => this.formGroup.get('exchangeRate').setValue(r))
        );

        this.salesManagerControl.valueChanges.pipe(
            takeUntil(this.unsubscribe$),
            filter(v => !(typeof v === 'string'))
        ).subscribe(v => this.formGroup.get('salesManager').setValue(v.email ?? null));

        this.formGroup.get('opportunityStage').valueChanges.pipe().subscribe(value => {
            switch (value) {
                case OpportunityStages.RICHIESTA_DI_OFFERTA:
                    this.formGroup.get('proposalRequested').setValue(new Date());
                    this.formGroup.get('proposalRequested').addValidators(Validators.required);
                    break;
                case OpportunityStages.OFFERTA_INVIATA:
                    this.formGroup.get('proposalSentOn').setValue(new Date());
                    this.formGroup.get('proposalSentOn').addValidators(Validators.required);
                    break;
                default:
                    this.formGroup.get('proposalRequested').clearValidators();
                    this.formGroup.get('proposalSentOn').clearValidators();
            }
            console.log(this.formGroup.invalid, this.companySearchControl.invalid, this.formGroup.getRawValue())
        });
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    public setCompany(c: Company | null) {
        this.selectedCompany = c;
        this.formGroup.get('companyName').setValue(c?.name || null);
        this.formGroup.get('contactId').setValue(c?.primary_contact_id || null);
        this.formGroup.get('companyId').setValue(c?.id || null);
    }

    save() {
        const formValue: Opportunity = this.formGroup.getRawValue();

        this.opportunityService.updateForecastAmountsValue(formValue);

        const cosmoId = (this.selectedCompany && this.selectedCompany.custom_fields) ?
            this.opportunityService.extractCosmoIdFromCompany(this.selectedCompany) : null;

        if (formValue.nextFollowUpDate && !(formValue.nextFollowUpDate instanceof Date)) {
            formValue.nextFollowUpDate = (formValue.nextFollowUpDate as Moment).toDate();
        }

        this.dialogRef.close({opportunity: formValue, cosmoId: cosmoId, createBudget: this.crateLinkedBudget.value});
    }
}
